import type { ProductionRequirementView } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { usePlan, usePlanWrite, writeError } from './context.js';
import { fieldLabel, formatPlanValue } from './format.js';

type Verdict = { verdict: 'ACCEPT' | 'REJECT'; keepApprovedQuantity?: true };

/**
 * WHAT CHANGED UNDER A DECISION, field by field.
 *
 * A requirement somebody accepted or rejected whose basis then moved reads
 * NEEDS_REVALIDATION, and the server refuses a plain Accept or Reject on it
 * (409 `REVALIDATION_REQUIRED`): the person decides again with the reviewed
 * value beside the current one. Three answers, all secondary — the scene's one
 * red button stays the screen's:
 *
 * - Accept current: accept the derivation as it is now (any approved count is
 *   cleared, because it was approved against the old one);
 * - Keep approved value: accept it and keep the approved count;
 * - Reject.
 */
export function RevalidationDiff({
  requirement,
  disabledReason,
}: {
  requirement: ProductionRequirementView;
  disabledReason: string | null;
}) {
  const { projectId, sceneLabel } = usePlan();
  const write = usePlanWrite((input: Verdict) =>
    api.decidePlanRequirement(projectId, requirement.id, {
      action: 'REVALIDATE',
      rowVersion: requirement.rowVersion,
      ...input,
    }),
  );
  const id = requirement.id;
  const blocked = write.isPending || disabledReason !== null;
  const reviewed = requirement.reviewed;

  return (
    <div className="pp-revalidation" data-testid={`production-revalidation-${id}`}>
      <p className="pp-revalidation-lede">
        {reviewed === null
          ? 'What this requirement rests on has changed. Check it again.'
          : `What this requirement rests on has changed since it was ${reviewed.decision === 'ACCEPTED' ? 'accepted' : 'rejected'}. Check it again.`}
      </p>

      {requirement.diff.length === 0 ? (
        <p className="mm pp-note">No field changed that the plan shows; only what it rests on did.</p>
      ) : (
        <table className="pp-diff">
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">When reviewed</th>
              <th scope="col">Now</th>
            </tr>
          </thead>
          <tbody>
            {requirement.diff.map((d) => (
              <tr key={d.field} data-testid={`production-revalidation-field-${id}-${d.field}`}>
                <th scope="row">{fieldLabel(d.field)}</th>
                <td>{formatPlanValue(d.field, d.reviewed, sceneLabel)}</td>
                <td>{formatPlanValue(d.field, d.current, sceneLabel)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="ui-toolbar">
        <button
          type="button"
          className="ctl"
          data-testid={`production-revalidate-accept-${id}`}
          disabled={blocked}
          title={disabledReason ?? undefined}
          onClick={() => {
            write.mutate({ verdict: 'ACCEPT' });
          }}
        >
          Accept current
        </button>
        {requirement.approvedQuantity === null ? null : (
          <button
            type="button"
            className="ctl"
            data-testid={`production-revalidate-keep-${id}`}
            disabled={blocked}
            title={disabledReason ?? undefined}
            onClick={() => {
              write.mutate({ verdict: 'ACCEPT', keepApprovedQuantity: true });
            }}
          >
            {`Keep approved value (${String(requirement.approvedQuantity)})`}
          </button>
        )}
        <button
          type="button"
          className="ctl"
          data-testid={`production-revalidate-reject-${id}`}
          disabled={blocked}
          title={disabledReason ?? undefined}
          onClick={() => {
            write.mutate({ verdict: 'REJECT' });
          }}
        >
          Reject
        </button>
      </div>

      {write.error === null ? null : (
        <p className="pp-error" role="alert" data-testid={`production-revalidation-error-${id}`}>
          {writeError(write.error)}
        </p>
      )}
    </div>
  );
}
